import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared-kernel/primary/shared/shadcn/ui/dialog";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { Checkbox } from "@shared-kernel/primary/shared/shadcn/ui/checkbox";
import { BillingAddressField } from "@shared-kernel/primary/shared/billing-address-field";
import { ReadOnlyField } from "@shared-kernel/primary/shared/shadcn/ui/read-only-field";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Loader2 } from "lucide-react";
import { Alert } from "@shared-kernel/primary/shared/alert";
import { formatIban, formatToCurrency } from "@utils/formatting";
import { ORGANIZATION_NAME } from "@shared-kernel/domain/organization";
import { useMemo, useState } from "react";
import { useAppDispatch } from "@redux/hooks";
import { useToast } from "@shared-kernel/primary/shared/shadcn/ui/use-toast";
import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";
import { PopoverCalendar } from "@shared-kernel/primary/shared/calendar/popover-calendar";
import { getMinFiscalPeriodDate } from "@shared-kernel/primary/shared/date/min-fiscal-period-date";
import { addMonths } from "date-fns";
import { commonAdapters } from "src/common-adapters";
import { uploadDocument } from "@shared-kernel/application/use-cases/document-upload/upload-document";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { teacherRequestTeacherPayout } from "@academy-context/write/application/use-cases/teacher/teacher-payout-request/request-teacher-payout";
import { teacherRetrievePayoutRequestEligibility } from "../../../read/application/use-cases/teacher/payout-request-eligibility/retrieve-payout-request-eligibility";
import type { TeacherVM } from "@shared-kernel/application/ports/teacher/teacher-repository";
import type { RequestState } from "@redux/state/shared-types";
import {
  createPayoutRequestSchema,
  defaultPayoutRequestValues,
  formatFormDataToBody,
  type PayoutRequestFormInputs,
} from "./form-validation/payout-request.schema";

interface Props {
  parentDialogOnOpenChange: (open: boolean) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hours: number;
  amount: number;
  profile: TeacherVM;
  uploadProcessing: RequestState;
}

export const PayoutRequestDialog = ({ open, onOpenChange, parentDialogOnOpenChange, hours, amount, profile, uploadProcessing }: Props) => {
  const dispatch = useAppDispatch();

  const { dateProvider } = commonAdapters;
  const now = useMemo(() => dateProvider!.now(), [dateProvider]);

  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<PayoutRequestFormInputs>({
    resolver: yupResolver(createPayoutRequestSchema(now)),
    defaultValues: { ...defaultPayoutRequestValues, invoiceDate: now, invoiceDeadline: addMonths(now, 1) },
  });

  const { handleSubmit, reset, setValue, watch, control } = form;

  const handleUpload = async (file: File) => {
    const url = await dispatch(
      uploadDocument({
        document: file,
        asAdmin: false,
        role: ROLES.TEACHER,
      }),
    ).unwrap();
    setValue("invoiceUrl", url, { shouldValidate: true });
    return url;
  };

  const onSubmit = async (formData: PayoutRequestFormInputs) => {
    try {
      setIsSubmitting(true);
      const body = formatFormDataToBody(formData);
      await dispatch(teacherRequestTeacherPayout(body)).unwrap();
      toast({
        title: "Demande envoyée",
        description: "Votre demande de règlement a bien été créée, elle sera traitée sous 48h.",
      });
      onOpenChange(false);
      parentDialogOnOpenChange(false);
      dispatch(teacherRetrievePayoutRequestEligibility());
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Une erreur est survenue lors de l'envoi de votre demande. Veuillez réessayer.";
      toast({
        variant: "destructive",
        title: "Erreur",
        description: errorMsg,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isSubmitting) {
          if (!isOpen) {
            reset({ ...defaultPayoutRequestValues, invoiceDate: now, invoiceDeadline: addMonths(now, 1) });
            setIsSubmitting(false);
          }
          onOpenChange(isOpen);
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Demande de règlement</DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Alert variant="error">
              <p className="text-sm">
                ATTENTION : Avant d'envoyer votre facture, vérifiez que le montant total correspond exactement à vos heures déclarées. En
                cas d'erreur, dirigez-vous vers la page des cours pour effectuer les corrections nécessaires{" "}
                <strong>avant de demander le règlement</strong>.
              </p>
              <p className="mt-2 text-sm font-bold">
                Merci d'émettre la facture au nom de {ORGANIZATION_NAME[profile.organization].long} et non de l'élève car nous ne pouvons
                pas payer une facture qui ne nous est pas adressée.
              </p>
            </Alert>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <BillingAddressField organization={profile.organization} />

                <ReadOnlyField label="IBAN de paiement">
                  {profile.iban ? <span>{formatIban(profile.iban)}</span> : <span className="text-muted-foreground">Non renseigné</span>}
                </ReadOnlyField>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <ReadOnlyField label="Nombre d'heures" inline>
                  {hours} heure(s)
                </ReadOnlyField>
                <ReadOnlyField label="Montant total" inline>
                  {formatToCurrency(amount)}
                </ReadOnlyField>
                <FormField
                  control={control}
                  name="invoiceNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Numéro de facture</FormLabel>
                      <FormControl>
                        <Input placeholder="Exemple: F-2024-001" maxLength={100} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={control}
                  name="invoiceDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date de facturation</FormLabel>
                      <FormControl>
                        <PopoverCalendar
                          now={now}
                          value={field.value}
                          onChange={field.onChange}
                          minDate={getMinFiscalPeriodDate(now)}
                          maxDate={now}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="invoiceDeadline"
                  render={({ field }) => {
                    const invoiceDate = watch("invoiceDate");
                    return (
                      <FormItem>
                        <FormLabel>Date d'échéance</FormLabel>
                        <FormControl>
                          <PopoverCalendar
                            now={now}
                            value={field.value}
                            onChange={field.onChange}
                            minDate={invoiceDate ? new Date(invoiceDate.getTime() + 86400000) : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>

              <FormField
                control={control}
                name="invoiceUrl"
                render={({ fieldState: { error } }) => (
                  <FormItem>
                    <UploadButton
                      onFileSelect={handleUpload}
                      accept={["application/pdf"]}
                      label="Télécharger votre facture (PDF) (obligatoire)"
                      helpText="Format accepté: PDF uniquement"
                      maxSize={10 * 1024 * 1024}
                      value={watch("invoiceUrl")}
                      showPreview
                      processing={uploadProcessing}
                      error={error?.message}
                    />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="isConfirmed"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="text-sm">
                        Je confirme que le montant de ma facture s'élève à {formatToCurrency(amount)} et que ma facture est bien adressée à{" "}
                        {ORGANIZATION_NAME[profile.organization].long}
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end">
              <Button className="w-full sm:w-auto" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  `Demander le règlement de ${formatToCurrency(amount)} (${hours}h)`
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
